<template>
	<div class="matchday--wrapper">
		<ListView
			:items="sortedFilteredEvents"
			:empty-msg="noEventsToday"
			:swipable="false"
		>
			<template #item-row="{ item: event }">
				<MatchdayListItem
					:event="event"
					:event-league="eventLeague"
					@play="handlePlay(event)"
				/>
			</template>
		</ListView>
	</div>
</template>

<script>
import MatchdayListItem from './MatchdayListItem';
import ListView from '@components/lists/ListView';
import { isPPV } from '@shared/utils/eventUtils';
import { MAX_INTERVAL } from '@videotools/js/constants/app';
import { mapActions, mapGetters } from 'vuex';
import laroute from '@laroute';

export default {
	name:       'MatchdayList',
	components: { ListView, MatchdayListItem },
	props:      {
		events: {
			type:    Array,
			default: () => [],
		},
		eventLeague: {
			type:     [Object, null],
			required: true,
			default:  () => {
			},
		},
		noEventsToday: {
			type: String,
		},
	},
	data() {
		return {
			eventTimers: [],
		};
	},
	computed: {
		...mapGetters({
			event:               'event/event',
			hasAccessToEvent:    'sharedUser/hasAccessToEvent',
			isTouchDevice:       'device/isTouchDevice',
			isMobileAppleDevice: 'device/isMobileAppleDevice',
		}),
		sortedFilteredEvents() {
			return this.events
				.map(e => {
					e.isActive = e.id === this.event.id;
					return e;
				})
				.slice()
				.sort((a, b) => {
					if (a.isLive === b.isLive) return new Date(b.start) - new Date(a.start);
					if (a.isLive) return -1;
					return 1;
				});
		},
		upcomingEvents() {
			return this.events.filter(ev => ev.liveInSeconds > 0);
		},
		liveEvents() {
			return this.events.filter(ev => ev.isLive);
		},
	},
	watch: {
		events() {
			this.clearAllTimeouts();
			this.setupUpcomingEventsLogic();
			this.setupLiveEventsLogic();
		},
	},
	mounted() {
		this.setupUpcomingEventsLogic();
		this.setupLiveEventsLogic();
	},
	destroyed() {
		this.clearAllTimeouts();
	},
	methods: {
		...mapActions({
			loadMatchdayEvents: 'event/loadMatchdayEvents',
		}),
		handlePlay(event) {
			if (event.isUpcoming) {
				this.$emit('redirect-to-upcoming-event', event);
			} else if (this.isMobileAppleDevice || !!event.externalEventLink) {
				this.redirectToEvent(event, true);
			} else {
				// hard redirect for unaccessible Resource in order to trigger the PaymentModal
				if (isPPV(event) && !this.hasAccessToEvent(event)) {
					this.redirectToEvent(event, true);
				} else {
					this.$store.dispatch('event/changeMatchday', event.id);
					this.redirectToEvent(event);
				}
			}
		},
		redirectToEvent(event, hardReload = false) {
			if (hardReload) {
				window.location.href = laroute.route('arena.event', {
					event: event.id,
					slug:  this.$Helpers.slugify(event.nameTranslations?.[this.$arenaConfig.defaultLanguage] || event.name),
				});
			} else {
				this.$router.push({
					name:   this.$route.name,
					params: {
						id:   event.id,
						slug: this.$Helpers.slugify(event.nameTranslations?.[this.$arenaConfig.defaultLanguage] || event.name),
					},
				}).catch(() => {
					// Double click throws an error because UI has not updated yet. We can ignore this error.
				});
			}
		},
		setupUpcomingEventsLogic() {
			for (const event of this.upcomingEvents) {
				this.setEventsRefreshTimeout(event.liveInSeconds);
			}
		},
		setupLiveEventsLogic() {
			for (const event of this.liveEvents) {
				// cancel if event.endTimestamp is undefined
				if (!event.endTimestamp) {
					continue;
				}
				const remainingTime = Math.ceil(event.endTimestamp - (Date.now() / 1000));
				const delay = Math.max(remainingTime, 0);
				this.setEventsRefreshTimeout(delay);
			}
		},
		setEventsRefreshTimeout(seconds) {
			const delay = (seconds + Math.round(Math.random() * 10)) * 1000; // adding a 10s random delay to avoid many concurrent requests at the same time
			let eventTimer;

			if (delay > MAX_INTERVAL) {
				eventTimer = setTimeout(() => {
					this.setEventsRefreshTimeout(delay - MAX_INTERVAL);
				}, MAX_INTERVAL);
			} else {
				eventTimer = setTimeout(() => {
					this.loadMatchdayEvents(true);
				}, delay);
			}

			this.eventTimers.push(eventTimer);
		},
		clearAllTimeouts() {
			for (const timer of this.eventTimers) {
				clearTimeout(timer);
			}
			this.eventTimers = [];
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.matchday--wrapper {
	height: 100%;
}

.matchday--wrapper::v-deep {
	.list--container {
			height: 100%;
	}
}

</style>
